import { StyleSheet, css } from 'aphrodite';
import { subDays } from 'date-fns';
import { observer } from 'mobx-react';
import { parseAsString, parseAsStringEnum, useQueryState } from 'nuqs';
import { useMemo } from 'react';
import { Redirect } from 'react-router-dom';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { TabToggle } from '../../../web/components-v2/TabToggle';
import { SPACE } from '../../../web/components-v2/Theme/theme';
import { bs } from '../../../web/styles/styles';
import { useManagerPermissions } from '../../hooks/useManagerPermissions';
import { ITimeEntryFilter, useTimeEntryFilterQuery, useTimeEntryReviewQueueQuery } from '../../queries/TimeEntry';
import { formatDayShort, todayYmd } from '../../timeTracking/days';
import { timeTrackingStyles as tt } from '../../timeTracking/styles';
import { useTimeTracking } from '../../timeTracking/useTimeTracking';
import { useUserNames } from '../../timeTracking/useUserNames';
import { AllEntriesTable } from './AllEntriesTable';
import { RepDaysDrawer } from './RepDaysDrawer';
import { ReviewQueueTable } from './ReviewQueueTable';
import { ALL, distinctOffices, distinctUserIds, matchesReviewFilters } from './reviewFilters';

const RECENT_DAYS = 30;
const VIEWS = [
	{ label: 'Review queue', value: 'queue' },
	{ label: 'All entries', value: 'all' },
] as const;

const viewParser = parseAsStringEnum(['queue', 'all']).withDefault('queue');
const filterParser = parseAsString.withDefault(ALL);
const repParser = parseAsString.withOptions({ history: 'push' });

function _HoursReview() {
	const { enabled, showHoursReview, canActAsSelf, isAdmin } = useTimeTracking();
	const { isLoading: isLoadingPermissions } = useManagerPermissions();
	const { nameFor, initialsFor } = useUserNames({ enabled });

	const [view, setView] = useQueryState('view', viewParser);
	const [rep, setRep] = useQueryState('rep', filterParser);
	const [office, setOffice] = useQueryState('office', filterParser);
	const [openRep, setOpenRep] = useQueryState('repDays', repParser);

	const queueQuery = useTimeEntryReviewQueueQuery({ enabled });
	const allFilter = useMemo<ITimeEntryFilter>(
		() => ({ criteria: [{ op: 'Gt', property: 'Day', value: todayYmd(subDays(new Date(), RECENT_DAYS)) }] }),
		[]
	);
	const allQuery = useTimeEntryFilterQuery({ enabled: enabled && view === 'all', filter: allFilter });

	const reviews = queueQuery.data?.values ?? [];
	const allEntries = allQuery.data?.values ?? [];
	const filters = { office, rep };
	const visibleReviews = reviews.filter(review => matchesReviewFilters(review.entry, filters));
	const visibleEntries = allEntries.filter(entry => matchesReviewFilters(entry, filters));
	const optionEntries = view === 'queue' ? reviews.map(review => review.entry) : allEntries;
	const repOptions = distinctUserIds(optionEntries)
		.map(userId => ({ label: nameFor(userId), value: userId }))
		.sort((a, b) => a.label.localeCompare(b.label));
	const officeOptions = distinctOffices(optionEntries);
	const showRepFilter = repOptions.length > 1 || rep !== ALL;
	const showOfficeFilter = officeOptions.length > 0 || office !== ALL;

	if (!enabled) {
		return <Redirect to='/' />;
	}
	if (isLoadingPermissions || (!showHoursReview && queueQuery.isLoading)) {
		return <LoadingSpinner type='large' />;
	}
	if (!showHoursReview) {
		return <Redirect to='/timesheet' />;
	}

	const oldest = reviews.map(review => review.entry.day).sort()[0];
	const waitingCount = queueQuery.data?.totalCount ?? reviews.length;
	const queueSummary =
		queueQuery.isLoading || queueQuery.isError
			? ''
			: waitingCount
				? `${waitingCount} ${waitingCount === 1 ? 'day' : 'days'} waiting · oldest ${formatDayShort(oldest)}`
				: 'Nothing waiting';

	return (
		<div className={css(tt.page)}>
			<h1 className={css(tt.pageTitle)}>Hours review</h1>
			<div className={css(styles.toolbar)}>
				<TabToggle
					items={[...VIEWS]}
					value={view}
					onValueChange={value => value && setView(value as 'queue' | 'all')}
					aria-label='Hours review view'
				/>
				{showRepFilter ? (
					<div className={css(styles.select)}>
						<Select
							value={rep}
							onValueChange={(value: string | null) => setRep(!value || value === ALL ? null : value)}
						>
							<SelectTrigger styles={[bs.bgWhite, bs.wFull]}>
								<SelectValue>{rep === ALL ? 'All reps' : nameFor(rep)}</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={ALL}>All reps</SelectItem>
								{repOptions.map(option => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				) : null}
				{showOfficeFilter ? (
					<div className={css(styles.select)}>
						<Select
							value={office}
							onValueChange={(value: string | null) => setOffice(!value || value === ALL ? null : value)}
						>
							<SelectTrigger styles={[bs.bgWhite, bs.wFull]}>
								<SelectValue>{office === ALL ? 'All offices' : office}</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={ALL}>All offices</SelectItem>
								{officeOptions.map(option => (
									<SelectItem key={option} value={option}>
										{option}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				) : null}
				<span className={css(tt.muted, styles.summary)}>
					{view === 'queue' ? queueSummary : `Last ${RECENT_DAYS} days`}
				</span>
			</div>

			{view === 'queue' ? (
				<ReviewQueueTable
					reviews={visibleReviews}
					isLoading={queueQuery.isLoading}
					isError={queueQuery.isError}
					onRetry={() => queueQuery.refetch()}
					canActAsSelf={canActAsSelf}
					nameFor={nameFor}
					initialsFor={initialsFor}
					onOpenRep={setOpenRep}
				/>
			) : (
				<AllEntriesTable
					entries={visibleEntries}
					isLoading={allQuery.isLoading}
					isError={allQuery.isError}
					onRetry={() => allQuery.refetch()}
					nameFor={nameFor}
					initialsFor={initialsFor}
					onOpenRep={setOpenRep}
				/>
			)}

			<RepDaysDrawer
				key={openRep ?? 'closed'}
				userId={openRep}
				isAdmin={isAdmin}
				canActAsSelf={canActAsSelf}
				nameFor={nameFor}
				onClose={() => setOpenRep(null)}
			/>
		</div>
	);
}

export const HoursReview = observer(_HoursReview);

const styles = StyleSheet.create({
	select: {
		width: 200,
	},
	summary: {
		marginLeft: 'auto',
	},
	toolbar: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: SPACE[12],
	},
});
